import { createSelector } from "reselect";
import { isDefined, compareValues, uniqueByKey, memoize } from "@ls/js-utils";
import SelectionState from "state/selections/SelectionState";
import {
  collectEntitySummaryInfo,
  getEntitySummariesObject,
  splitByCalcType,
} from "state/util";
import scope from "./scope";
import SummaryType from "state/selections/Constants/SummaryType";
import { selectSideBySideValue } from "state/selections/selector";
export const selectAnalyticsRatioData = (state) =>
  state[scope]["analyticsRatioData"];

export const selectDefinitions = memoize((selectionState) => {
  if (selectionState === SelectionState.UNCOMMITTED) {
    console.log("Returning uncommitted definitions");
    return (state) => state[scope]["definitions"][selectionState];
  }

  return createSelector(
    (state) => state[scope]["definitions"][selectionState],
    (definitions) => {
      const completeDefinitions = definitions.filter(
        ({ complete }) => complete,
      );
      console.log("Analytics - completeDefinitions:", completeDefinitions);
      return completeDefinitions.filter(
        uniqueByKey(completeDefinitions, "key"),
      );
    },
  );
});

export const selectAnalyticsData = (summaryType) => (state) =>
  state[scope]["analyticsData"][summaryType];
export const selectFetchingAnalyticsData = (summaryType) => (state) =>
  state[scope]["fetchingAnalyticsData"][summaryType];
export const selectAnalyticsSbsData = (summaryType) => (state) =>
  state[scope]["analyticsSbsData"][summaryType];
export const selectFetchingAnalyticsSbsData = (summaryType) => (state) =>
  state[scope]["fetchingAnalyticsSbsData"][summaryType];

export const findAnalyticValue = (
  node,
  definition,
  accEntityId,
  bmkEntityId,
) => {
  const { analytic, summary } = definition;
  const entityId =
    summary === SummaryType.BENCHMARK ? bmkEntityId : accEntityId;
  const isRelative = summary === SummaryType.RELATIVE;
  const entitySummariesObject =
    getEntitySummariesObject(node, entityId, isRelative) ?? {};

  const analyticValueMap = entitySummariesObject?.["values"] ?? [];
  return analyticValueMap?.[analytic] ?? null;
};

export const selectRowsMap = createSelector(
  selectDefinitions(SelectionState.COMMITTED),
  selectAnalyticsData(SummaryType.ABSOLUTE),
  selectAnalyticsData(SummaryType.RELATIVE),
  selectAnalyticsSbsData(SummaryType.ABSOLUTE),
  selectAnalyticsSbsData(SummaryType.RELATIVE),
  selectAnalyticsRatioData,
  selectSideBySideValue(SelectionState.COMMITTED),
  (
    definitions,
    absData,
    relData,
    absSbsData,
    relSbsData,
    ratioData,
    sideBySide,
  ) => {
    const absEntitySummaries = absData?.root?.entitySummaries ?? [];
    const relEntitySummaries = relData?.root?.entitySummaries ?? [];
    const absSbsEntitySummaries = absSbsData?.root?.entitySummaries ?? [];
    const relSbsEntitySummaries = relSbsData?.root?.entitySummaries ?? [];
    const ratioEntitySummaries = ratioData?.root?.entitySummaries ?? [];
    const { bmkEntityIdSet: absBmkIdSet, bmkSummaryMap: bmkIdSet } =
      collectEntitySummaryInfo(absEntitySummaries);
    const {
      entitySummaryMap: relEntitySummaryMap,
      bmkEntityIdSet: relBmkIdSet,
    } = collectEntitySummaryInfo(relEntitySummaries);
    const bmkEntityIdSet = new Set([...absBmkIdSet, ...relBmkIdSet]);
    const [absDefinitions, relDefinitions, bmkDefinitions] =
      splitByCalcType(definitions);
    const ratioDefinitions = relDefinitions.filter(
      (d) => d.scaleType === "RATIO",
    );
    const relNonRatioDefinitions = relDefinitions.filter(
      (d) => d.scaleType !== "RATIO",
    );
    const entityRowsMap = {};

    const percentileScaleTypes = new Set(["25", "75", "Median"]);
    const getValueForDefinition = (values, percentiles, def) => {
      const { analytic, scaleType } = def;
      if (percentileScaleTypes.has(scaleType)) {
        const target = scaleType === "Median" ? 50 : Number(scaleType);
        const found = Array.isArray(percentiles)
          ? percentiles.find((p) => p?.percentile === target)
          : undefined;
        return found ? (found[analytic] ?? null) : null;
      }
      return values?.[analytic] ?? null;
    };

    /**Functions for handling abs/rel definitions**/
    const handleAbsDefinitions = (definitions, entitySummaries, scenario) => {
      definitions.forEach((definition) => {
        const { analytic, summary, scaleType } = definition;
        entitySummaries.forEach((entitySummary) => {
          const { values, entityResultMetaData } = entitySummary;
          const { entityId } = entityResultMetaData;
          const baseColId = `${analytic},${summary},${scaleType}`;
          const colId = `${baseColId}${scenario ? ` - scenario` : ""}`;

          if (!entityRowsMap[entityId]) {
            entityRowsMap[entityId] = {};
          }
          entityRowsMap[entityId][colId] = getValueForDefinition(
            values,
            entitySummary?.percentiles,
            definition,
          );
        });
      });
    };
    const handleRelDefinitions = (definitions, entitySummaries, scenario) => {
      definitions.forEach((definition) => {
        const { analytic, summary, scaleType } = definition;
        entitySummaries.forEach((entitySummary) => {
          const { values, entityResultMetaData } = entitySummary;
          const { entityId } = entityResultMetaData;
          const baseColId = `${analytic},${summary},${scaleType}`;
          const colId = `${baseColId}${scenario ? ` - scenario` : ""}`;
          if (!entityRowsMap[entityId]) {
            entityRowsMap[entityId] = {};
          }
          if (
            bmkEntityIdSet.has(entityId) &&
            isDefined(entityRowsMap[entityId][colId])
          ) {
            //for benchmarks, make sure value doesn't exist already since we want to honor absolute values first
            return;
          }
          entityRowsMap[entityId][colId] = getValueForDefinition(
            values,
            entitySummary?.percentiles,
            definition,
          );
        });
      });
    };

    const handleRatioDefinitions = (definitions, entitySummaries) => {
      definitions.forEach((definition) => {
        const { analytic, summary, scaleType } = definition; // scaleType should be "RATIO"
        entitySummaries.forEach((entitySummary) => {
          const { values, entityResultMetaData } = entitySummary;
          const { entityId } = entityResultMetaData;
          const baseColId = `${analytic},${summary},${scaleType}`;
          const colId = baseColId;
          if (!entityRowsMap[entityId]) {
            entityRowsMap[entityId] = {};
          }
          entityRowsMap[entityId][colId] = values?.[analytic] ?? null;
        });
      });
    };

    /**Handle abs and rel definitions**/
    handleAbsDefinitions(absDefinitions, absEntitySummaries);
    handleRelDefinitions(relNonRatioDefinitions, relEntitySummaries);
    handleRatioDefinitions(ratioDefinitions, ratioEntitySummaries);

    /**handle side by side data, if enabled**/
    if (sideBySide) {
      handleAbsDefinitions(absDefinitions, absSbsEntitySummaries, true);
      handleRelDefinitions(relDefinitions, relSbsEntitySummaries, true);
    }

    /**now handle benchmark definitions**/
    bmkDefinitions.forEach((definition) => {
      const { analytic, summary, scaleType } = definition;
      [...absEntitySummaries, ...relEntitySummaries].forEach(
        (entitySummary) => {
          const { entityResultMetaData } = entitySummary;
          const { entityId, benchId } = entityResultMetaData;
          const colId = `${analytic},${summary},${scaleType}`;

          if (!entityRowsMap[entityId]?.[colId]) {
            const bmkEntitySummary =
              bmkIdSet.get(benchId) ?? relEntitySummaryMap.get(benchId);
            if (!entityRowsMap[entityId]) {
              entityRowsMap[entityId] = {};
            }
            if (
              bmkEntityIdSet.has(entityId) &&
              isDefined(entityRowsMap[entityId]?.[colId])
            ) {
              //for benchmarks, make sure value doesn't exist already since we want to honor absolute values first
              return;
            }
            entityRowsMap[entityId][colId] = getValueForDefinition(
              bmkEntitySummary?.values ?? {},
              bmkEntitySummary?.percentiles ?? [],
              definition,
            );
          }
        },
      );
    });
    return entityRowsMap;
  },
);

export const selectFetchingAnalyticValues = (state) =>
  state[scope].fetchingAnalyticValues;
export const selectAnalyticValues = (state) => state[scope].analyticValues;
export const selectAnalyticValuesSelectionOptions = createSelector(
  selectAnalyticValues,
  (analyticValues) => {
    if (analyticValues && analyticValues.length > 0) {
      return analyticValues
        .map((aValue) => ({ label: aValue, value: aValue }))
        .sort(compareValues("label"));
    }
    return [];
  },
);
